export type {
  NWTEvent,
  NWTExtendedEvent,
  NWTBasicEvent,
  NWTEcommerceEvent,
  NWTExperimentEvent,
  NWTAiComponentEvent,
  NWTMessageEvent,
  NWTSearchResultEvent,
} from './nw-tracking-types';

export type { Events, ExitIntentTrackingOptions, TrackingEvent } from './types';

import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import snakeCaseKeys from 'snakecase-keys';

import { Eventual } from '@xing-com/crate-xinglet';
import type { ViewerData } from '@xing-com/hub';
import { useExperimentTrackingData, useViewerData } from '@xing-com/hub';
import ttt from '@xing-com/ticktricktrack';

import { SCHEMA_VERSION as NWT_SCHEMA_VERSION } from './nw-tracking-types';
import type {
  ExitIntentTrackingOptions,
  TrackingEvent,
  UseTrackingReturn,
} from './types';

export const SCHEMA_VERSION = '1.3.0';

const MOBILE_BREAKPOINT = 739;
const isMobile = (): boolean =>
  typeof globalThis.window !== 'undefined' &&
  globalThis.window.innerWidth <= MOBILE_BREAKPOINT;

const EXIT_INTENT_SCROLL_TIME_WINDOW = 500;
const EXIT_INTENT_SCROLL_VIEWPORT_RATIO = 0.5;

let currentPageView = { key: 0, page: undefined as string | undefined };

function handleTrackingEvent(
  payload: TrackingEvent,
  {
    locale,
    userId,
    PropExperiment,
    PropMemberships,
  }: {
    locale: string;
    userId?: string;
    PropExperiment?: string;
    PropMemberships?: string;
  }
): void {
  const { type, ...event } = payload;

  event.channel = isMobile() ? 'webm' : 'web';
  event.sdkName = 'crate';
  event.applicationLanguage = locale;

  if (PropExperiment) {
    event.experiment = PropExperiment;
  }

  const subscriptions = [PropMemberships, event.subscriptions]
    .filter(Boolean)
    .join(',');

  if (subscriptions) {
    event.subscriptions = subscriptions;
  }

  if (!event.userId && userId) {
    // TODO: check why the UserId can be a string or a number
    event.userId = `${userId}`;
  }

  // Overwrite the schema version with the current one defined by the nwt tracking lib
  event.schemaVersion = NWT_SCHEMA_VERSION;

  ttt.nwtEvent(snakeCaseKeys(event));
}

let resolve: ((value: ViewerData) => void) | undefined;
let promise: Eventual<ViewerData> | undefined;

if (typeof window !== 'undefined') {
  promise = new Eventual<ViewerData>((res) => {
    resolve = res;
  });
}

export const useViewerDataEventual = (): Eventual<ViewerData> | undefined => {
  const { data: viewerData } = useViewerData();

  if (resolve && viewerData) {
    resolve(viewerData);
    resolve = undefined;
  }

  return promise;
};

export const useTracking = (): UseTrackingReturn => {
  const { locale } = useIntl();
  const { PropExperiment } = useExperimentTrackingData();
  const userDataEventual = useViewerDataEventual();

  const track = useCallback(
    (payload: TrackingEvent): void => {
      if (payload.event === 'viewed_screen' && payload.page) {
        currentPageView = {
          key: currentPageView.key + 1,
          page: payload.page,
        };
      }

      userDataEventual?.then((viewerData) => {
        const { user, propMemberships } = viewerData;

        handleTrackingEvent(payload, {
          locale,
          PropExperiment,
          PropMemberships: propMemberships,
          userId: user?.id,
        });
      });
    },
    [userDataEventual, locale, PropExperiment]
  );

  return { track };
};

export const useExitIntentTracking = ({
  enabled,
  page,
}: ExitIntentTrackingOptions): void => {
  const { track } = useTracking();
  const trackedPageViewRef = useRef<number | string>();

  const trackExitIntent = useCallback(
    (context: string) => {
      const trackedPage = page ?? currentPageView.page;
      const pageViewKey =
        currentPageView.page === trackedPage
          ? currentPageView.key
          : `configured:${trackedPage}`;

      if (!trackedPage || trackedPageViewRef.current === pageViewKey) {
        return;
      }

      trackedPageViewRef.current = pageViewKey;

      track({
        type: 'nwt',
        event: 'left_viewport',
        eventSchema: 'basic',
        application: 'wbm',
        sentBy: 'platform',
        page: trackedPage,
        context,
      });
    },
    [page, track]
  );

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    if (!isMobile()) {
      const handleMouseLeave = (event: MouseEvent): void => {
        if (event.clientY <= 0) {
          trackExitIntent('exit_intent_browser_top');
        }
      };

      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }

    let scrollStart: { time: number; scrollY: number } | undefined;

    const handleScroll = (): void => {
      const now = Date.now();
      const currentScrollY = window.scrollY;
      const threshold = window.innerHeight * EXIT_INTENT_SCROLL_VIEWPORT_RATIO;

      if (
        !scrollStart ||
        now - scrollStart.time > EXIT_INTENT_SCROLL_TIME_WINDOW ||
        currentScrollY > scrollStart.scrollY
      ) {
        scrollStart = { time: now, scrollY: currentScrollY };

        return;
      }

      if (scrollStart.scrollY - currentScrollY >= threshold) {
        trackExitIntent('exit_intent_rapid_scroll');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, trackExitIntent]);
};
